<template>
  <section class="pb-0 pt-0 sm:px-6 sm:pb-4 sm:pt-3">
    <div class="flex h-[calc(100vh-4.5rem)] min-h-[100dvh] w-full overflow-hidden border-y border-white/10 bg-midnight-900/95 shadow-glow sm:mx-auto sm:h-[calc(100vh-7.5rem)] sm:min-h-[760px] sm:max-w-[1500px] sm:rounded-[2rem] sm:border sm:border-white/10">
      <aside
        v-if="!isMobileViewport || !activeConversation"
        class="flex w-full max-w-none flex-col bg-midnight-950/95 lg:max-w-[390px] lg:border-r lg:border-white/10"
      >
        <div class="border-b border-white/10 px-6 py-5">
          <div class="flex items-center justify-between gap-3">
            <div>
              <p class="text-[11px] uppercase tracking-[0.35em] text-steel-500">{{ $t('messagesPage.eyebrow') }}</p>
              <h1 class="mt-2 font-display text-2xl font-semibold text-white">{{ $t('messagesPage.title') }}</h1>
              <p class="mt-1 text-sm text-steel-400">{{ conversations.length === 1 ? $t('messagesPage.conversationCount_one') : $t('messagesPage.conversationCount_other', { count: conversations.length }) }}</p>
            </div>
            <router-link
              v-if="activeConversation && activeConversation.otherUserHandle"
              :to="`/u/${activeConversation.otherUserHandle}`"
              class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-neon-300 transition hover:border-white/20 hover:bg-white/10"
            >
              {{ $t('messagesPage.viewProfile') }}
            </router-link>
          </div>

          <div class="mt-5 grid grid-cols-3 gap-2 rounded-2xl border border-white/10 bg-white/[0.03] p-1 text-[11px] uppercase tracking-[0.28em] text-steel-400">
            <button type="button" class="rounded-xl bg-white px-3 py-2 font-semibold text-midnight-900">{{ $t('messagesPage.primary') }}</button>
            <button type="button" class="rounded-xl px-3 py-2 transition hover:bg-white/5">{{ $t('messagesPage.general') }}</button>
            <button type="button" class="rounded-xl px-3 py-2 transition hover:bg-white/5">{{ $t('messagesPage.requests') }}</button>
          </div>

          <div class="mt-4">
            <input
              v-model.trim="conversationSearch"
              type="text"
              :placeholder="$t('messagesPage.searchPlaceholder')"
              class="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-steel-500 focus:border-neon-400/60 focus:outline-none focus:ring-2 focus:ring-neon-400/20"
            />
          </div>
        </div>

        <div class="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          <div v-if="loadingConversations" class="space-y-2 px-2 py-4">
            <div
              v-for="item in 5"
              :key="`conversation-skeleton-${item}`"
              class="flex items-center gap-3 rounded-[1.6rem] border border-white/10 bg-midnight-900/55 px-4 py-4 animate-pulse"
            >
              <div class="h-14 w-14 rounded-2xl bg-white/10"></div>
              <div class="min-w-0 flex-1 space-y-2">
                <div class="h-3 w-32 rounded bg-white/10"></div>
                <div class="h-2 w-3/4 rounded bg-white/10"></div>
                <div class="h-2 w-24 rounded bg-white/10"></div>
              </div>
            </div>
          </div>
          <div v-else-if="!filteredConversations.length" class="px-2 py-6 text-sm text-steel-300">
            {{ $t('messagesPage.empty') }}
          </div>
          <div v-else class="space-y-2">
          <button
            v-for="conversation in filteredConversations"
            :key="conversation.id"
            type="button"
            class="flex w-full items-center gap-3 rounded-[1.6rem] border px-4 py-4 text-left transition"
            :class="activeConversationId === conversation.id ? 'border-white/25 bg-white/12 shadow-[0_12px_30px_rgba(0,0,0,0.18)]' : 'border-white/10 bg-midnight-900/55 hover:border-white/20 hover:bg-white/[0.04]'"
            @click="openConversation(conversation.id)"
          >
            <div class="relative h-14 w-14 overflow-hidden rounded-2xl border border-white/10 bg-midnight-900/70">
              <img
                v-if="conversation.otherUserAvatar"
                :src="conversation.otherUserAvatar"
                alt=""
                class="h-full w-full object-cover"
                referrerpolicy="no-referrer"
                crossorigin="anonymous"
              />
              <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">
                {{ conversation.otherUserInitials }}
              </div>
              <span
                v-if="conversation.unreadCount"
                class="absolute right-0 top-0 h-3.5 w-3.5 rounded-full border-2 border-midnight-900 bg-neon-400"
              ></span>
              <span
                v-if="conversation.isTyping"
                class="absolute -bottom-1 -right-1 rounded-full border border-midnight-900 bg-neon-400 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.18em] text-midnight-900"
              >
                {{ $t('messagesPage.typing') }}
              </span>
            </div>
            <div class="min-w-0 flex-1">
              <div class="flex items-center justify-between gap-3">
                <p class="truncate text-base font-semibold text-white">{{ conversation.otherUserName }}</p>
                <p class="shrink-0 text-[10px] uppercase tracking-[0.25em] text-steel-500">{{ conversation.lastMessageAtLabel }}</p>
              </div>
              <p class="mt-1 truncate text-sm text-steel-300">
                {{ conversationPreview(conversation) }}
              </p>
              <p class="mt-1 text-[11px] text-steel-500">
                {{ conversationActivityLabel(conversation) }}
              </p>
            </div>
            <span
              v-if="conversation.unreadCount"
              class="inline-flex min-w-[24px] items-center justify-center rounded-full bg-rose-500 px-2 py-1 text-[10px] font-semibold text-white"
            >
              {{ conversation.unreadCount }}
            </span>
          </button>
          </div>
        </div>
      </aside>

      <div
        v-if="!isMobileViewport || activeConversation"
        class="flex min-w-0 flex-1 flex-col bg-midnight-900"
      >
        <div v-if="activeConversation" class="flex h-full min-h-[620px] flex-col">
          <div class="relative flex flex-col gap-4 border-b border-white/10 bg-midnight-900/98 px-4 py-4 sm:px-6 lg:px-8 lg:py-5">
            <div class="flex items-start gap-2 pr-12 sm:gap-3">
            <button
              v-if="isMobileViewport"
              type="button"
              class="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] text-steel-300 transition hover:border-white/20 hover:text-white"
              @click="goBackToInbox"
            >
              <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M15 18l-6-6 6-6" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>
            <div class="h-12 w-12 shrink-0 overflow-hidden rounded-xl border border-white/10 bg-midnight-900/70 sm:h-16 sm:w-16 sm:rounded-2xl">
              <img
                v-if="activeConversation.otherUserAvatar"
                :src="activeConversation.otherUserAvatar"
                alt=""
                class="h-full w-full object-cover"
                referrerpolicy="no-referrer"
                crossorigin="anonymous"
              />
              <div v-else class="flex h-full w-full items-center justify-center text-base font-semibold text-steel-200">
                {{ activeConversation.otherUserInitials }}
              </div>
            </div>
            <div class="min-w-0 flex-1 pr-2">
              <p class="truncate text-lg font-semibold text-white sm:text-2xl">{{ activeConversation.otherUserName }}</p>
              <p class="mt-0.5 text-[11px] uppercase tracking-[0.24em] text-steel-400 sm:mt-1 sm:text-xs sm:tracking-[0.3em]">@{{ activeConversation.otherUserHandle }}</p>
              <p class="mt-1 text-[11px] text-neon-300 sm:mt-2 sm:text-xs" v-if="activeConversation.isTyping">{{ $t('messagesPage.typingLower') }}</p>
              <p class="mt-1 text-[11px] text-neon-300 sm:mt-2 sm:text-xs" v-else>{{ onlineStatusLabel }}</p>
              <p class="mt-0.5 text-[11px] text-steel-400 sm:mt-1 sm:text-xs">{{ activeConversationStatus }}</p>
            </div>
            </div>

            <div class="absolute right-4 top-4 sm:right-6 lg:right-8">
              <div class="relative">
                <button
                  type="button"
                  class="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-steel-300 transition hover:border-white/20 hover:bg-white/[0.05] hover:text-white sm:h-11 sm:w-11 sm:rounded-2xl"
                  @click="showConversationMenu = !showConversationMenu"
                >
                  <svg class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <circle cx="5" cy="12" r="1.8" />
                    <circle cx="12" cy="12" r="1.8" />
                    <circle cx="19" cy="12" r="1.8" />
                  </svg>
                </button>

                <div
                  v-if="showConversationMenu"
                  class="absolute right-0 top-12 z-20 w-52 rounded-3xl border border-white/10 bg-black p-3 shadow-glow sm:top-14"
                >
                  <p class="mb-2 px-2 text-[10px] uppercase tracking-[0.3em] text-steel-500">{{ $t('messagesPage.autoDelete') }}</p>
                  <div class="space-y-2">
                    <button
                      type="button"
                      class="w-full rounded-2xl border px-3 py-3 text-[10px] font-semibold uppercase tracking-[0.25em] transition"
                      :class="retentionButtonClass(3)"
                      :disabled="!retentionFeatureReady || savingRetention"
                      @click="updateConversationRetention(3)"
                    >
                      {{ $t('messagesPage.deleteIn3Hr') }}
                    </button>
                    <button
                      type="button"
                      class="w-full rounded-2xl border px-3 py-3 text-[10px] font-semibold uppercase tracking-[0.25em] transition"
                      :class="retentionButtonClass(24)"
                      :disabled="!retentionFeatureReady || savingRetention"
                      @click="updateConversationRetention(24)"
                    >
                      {{ $t('messagesPage.deleteIn24Hr') }}
                    </button>
                    <button
                      type="button"
                      class="w-full rounded-2xl border px-3 py-3 text-[10px] font-semibold uppercase tracking-[0.25em] transition"
                      :class="retentionButtonClass(168)"
                      :disabled="!retentionFeatureReady || savingRetention"
                      @click="updateConversationRetention(168)"
                    >
                      {{ $t('messagesPage.deleteIn1Week') }}
                    </button>
                    <button
                      type="button"
                      class="w-full rounded-2xl border border-rose-500/30 bg-rose-500/10 px-3 py-3 text-[10px] font-semibold uppercase tracking-[0.25em] text-rose-100 transition hover:bg-rose-500/20"
                      @click="deleteConversationNow"
                    >
                      {{ $t('messagesPage.deleteNow') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
              <div></div>
            </div>
          </div>

          <div ref="messagesScroller" class="flex-1 space-y-4 overflow-y-auto bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.05),_transparent_40%),linear-gradient(180deg,rgba(255,255,255,0.02),transparent)] px-4 py-5 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
            <div v-if="loadingMessages" class="space-y-4">
              <div v-for="item in 4" :key="`message-skeleton-${item}`" class="flex" :class="item % 2 === 0 ? 'justify-end' : 'justify-start'">
                <div class="max-w-[92%] rounded-[1.8rem] border border-white/10 px-4 py-4 sm:max-w-[72%] lg:max-w-[68%] animate-pulse" :class="item % 2 === 0 ? 'bg-white/10' : 'bg-midnight-950/85'">
                  <div class="space-y-2">
                    <div class="h-3 w-56 rounded bg-white/10"></div>
                    <div class="h-3 w-40 rounded bg-white/10"></div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else-if="!messages.length" class="rounded-3xl border border-dashed border-white/10 bg-white/[0.03] px-5 py-12 text-center text-sm text-steel-300">
              {{ $t('messagesPage.noMessages') }}
            </div>
            <div v-else>
              <template v-for="group in groupedMessages" :key="group.key">
                <div class="mb-5 flex justify-center">
                  <span class="rounded-full border border-white/10 bg-midnight-900/60 px-3 py-1 text-[10px] uppercase tracking-[0.28em] text-steel-400">
                    {{ group.label }}
                  </span>
                </div>
                <div
                  v-for="message in group.messages"
                  :key="message.id"
                  class="mb-5 flex"
                  :class="message.isMine ? 'justify-end' : 'justify-start'"
                >
                  <div class="max-w-[92%] rounded-[1.8rem] border px-4 py-4 shadow-[0_14px_30px_rgba(0,0,0,0.12)] sm:max-w-[72%] lg:max-w-[68%]"
                    :class="message.isMine ? 'border-white/10 bg-white text-midnight-900' : 'border-white/10 bg-midnight-950/85 text-white'">
                    <p class="whitespace-pre-wrap break-words text-[15px] leading-7">{{ message.body }}</p>
                    <div class="mt-2 flex items-center justify-end gap-2 text-[11px]"
                      :class="message.isMine ? 'text-midnight-700/80' : 'text-steel-400'">
                      <span>{{ message.timeLabel }}</span>
                      <span v-if="message.isMine">{{ message.statusLabel }}</span>
                    </div>
                  </div>
                </div>
              </template>

              <div v-if="activeConversation && activeConversation.isTyping" class="mt-2 flex justify-start">
                <div class="inline-flex items-center gap-3 rounded-[1.6rem] border border-white/10 bg-midnight-950/85 px-4 py-3 text-sm text-steel-200 shadow-[0_14px_30px_rgba(0,0,0,0.12)]">
                  <div class="flex items-center gap-1">
                    <span class="h-2 w-2 animate-bounce rounded-full bg-neon-400 [animation-delay:-0.2s]"></span>
                    <span class="h-2 w-2 animate-bounce rounded-full bg-neon-400 [animation-delay:-0.1s]"></span>
                    <span class="h-2 w-2 animate-bounce rounded-full bg-neon-400"></span>
                  </div>
                  <span>{{ activeConversation.otherUserName }} is typing...</span>
                </div>
              </div>
            </div>
          </div>

          <div class="border-t border-white/10 bg-midnight-900/98 px-4 py-4 sm:px-6 lg:px-8 lg:py-5">
            <form class="space-y-3" @submit.prevent="sendMessage">
              <div class="flex items-end gap-3 rounded-[2rem] border border-white/10 bg-white/[0.04] p-3">
                <button
                  type="button"
                  class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/10 bg-midnight-900/70 text-steel-300 transition hover:border-white/20 hover:text-white"
                >
                  <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <circle cx="12" cy="12" r="9" stroke-width="1.6" />
                    <path d="M8.5 14.5c.9 1 2.08 1.5 3.5 1.5s2.6-.5 3.5-1.5M9 10.5h.01M15 10.5h.01" stroke-width="1.6" stroke-linecap="round" />
                  </svg>
                </button>
                <textarea
                  v-model="draftMessage"
                  rows="1"
                  class="max-h-40 min-h-[52px] flex-1 resize-none bg-transparent px-2 py-3 text-[15px] text-white placeholder:text-steel-500 focus:outline-none"
                  placeholder="Message..."
                  @input="handleDraftInput"
                  @keydown="handleComposerKeydown"
                ></textarea>
                <button
                  type="submit"
                  class="rounded-2xl bg-white px-5 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-midnight-900 transition hover:bg-neon-400 disabled:cursor-not-allowed disabled:opacity-60"
                  :disabled="sendingMessage || !draftMessage.trim() || !activeConversation"
                >
                  {{ sendingMessage ? 'Sending...' : 'Send' }}
                </button>
              </div>
              <div class="flex items-center justify-between gap-3 px-2">
                <p class="text-xs text-steel-400">
                  {{ sendStateLabel }}
                </p>
                <p class="text-[11px] uppercase tracking-[0.28em] text-steel-500">DRVN Direct</p>
              </div>
            </form>
          </div>
        </div>

        <div v-else class="flex h-full min-h-[620px] items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.05),_transparent_35%)] px-8 text-center">
          <div class="max-w-md">
            <p class="text-xs uppercase tracking-[0.35em] text-steel-400">No chat selected</p>
            <h2 class="mt-3 font-display text-2xl font-semibold text-white">Pick a conversation</h2>
            <p class="mt-3 text-sm text-steel-300">
              Open a thread from the inbox, or visit a member profile and tap Message.
            </p>
          </div>
        </div>
      </div>
    </div>

    <p v-if="errorMessage" class="mx-auto mt-4 max-w-[1500px] rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
      {{ errorMessage }}
    </p>
  </section>
</template>

<script>
import { supabase, getAvatarUrl, refreshReadySession, runSupabaseQueryWithRetry } from '@/lib/supabase'

export default {
  name: 'MessagesView',
  data() {
    return {
      userId: '',
      myProfile: null,
      conversations: [],
      conversationSearch: '',
      activeConversationId: '',
      messages: [],
      loadingConversations: false,
      loadingMessages: false,
      savingRetention: false,
      sendingMessage: false,
      draftMessage: '',
      errorMessage: '',
      retentionFeatureReady: true,
      messagesChannel: null,
      typingChannel: null,
      conversationsChannel: null,
      presenceChannel: null,
      isOtherUserOnline: false,
      isMobileViewport: false,
      showConversationMenu: false,
      liveSyncTimer: null,
      typingStopTimer: null,
      typingSyncTimer: null,
    }
  },
  computed: {
    activeConversation() {
      return this.conversations.find((conversation) => conversation.id === this.activeConversationId) || null
    },
    filteredConversations() {
      const query = this.conversationSearch.trim().toLowerCase()
      if (!query) return this.conversations
      return this.conversations.filter((conversation) => {
        const haystack = [
          conversation.otherUserName,
          conversation.otherUserHandle,
          conversation.lastMessageBody,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        return haystack.includes(query)
      })
    },
    activeConversationStatus() {
      if (!this.activeConversation) return ''
      const lastMessage = this.messages[this.messages.length - 1]
      if (!lastMessage) return 'Conversation ready'
      if (this.activeConversation.isTyping) return 'Typing...'
      if (lastMessage.isMine) return this.messageReceiptLabel(lastMessage)
      return `Last message ${this.relativeTimeLabel(lastMessage.created_at)}`
    },
    sendStateLabel() {
      if (!this.activeConversation) return 'Select a conversation to start chatting.'
      if (this.savingRetention) return 'Saving auto-delete setting...'
      return 'Messages stay private through authenticated access rules and live delivery states.'
    },
    groupedMessages() {
      const groups = []
      const groupMap = new Map()
      this.messages.forEach((message) => {
        const key = new Date(message.created_at).toDateString()
        if (!groupMap.has(key)) {
          const group = {
            key,
            label: this.dayLabel(message.created_at),
            messages: [],
          }
          groupMap.set(key, group)
          groups.push(group)
        }
        groupMap.get(key).messages.push(message)
      })
      return groups
    },
    onlineStatusLabel() {
      if (this.activeConversation?.isTyping) return 'Typing'
      if (this.isOtherUserOnline) return 'Active now'
      if (this.activeConversation?.lastActiveAt) {
        return `Active ${this.relativeTimeLabel(this.activeConversation.lastActiveAt)}`
      }
      return 'Offline'
    },
  },
  async mounted() {
    const session = await refreshReadySession()
    this.userId = session?.user?.id || ''
    if (!this.userId) {
      this.errorMessage = 'Please sign in to use messages.'
      return
    }

    await this.fetchMyProfile()
    await this.purgeExpiredConversations()
    await this.fetchConversations()
    await this.openConversationFromRoute()
    this.subscribeToRealtime()
    this.handleViewportResize()
    this.startLiveSync()

    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    window.addEventListener('resize', this.handleViewportResize)
  },
  beforeUnmount() {
    this.clearTypingTimers()
    this.stopLiveSync()
    this.unsubscribeRealtime()
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    window.removeEventListener('resize', this.handleViewportResize)
  },
  watch: {
    '$route.params.handle': async function() {
      await this.openConversationFromRoute()
    },
  },
  methods: {
    async fetchMyProfile() {
      const { data } = await supabase
        .from('profiles')
        .select('id, handle, full_name, avatar_url, avatar_path')
        .eq('id', this.userId)
        .maybeSingle()

      this.myProfile = data || null
    },
    handleViewportResize() {
      if (typeof window === 'undefined') return
      this.isMobileViewport = window.innerWidth < 1024
    },
    initialsForName(name) {
      return (name || 'D')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((chunk) => chunk[0].toUpperCase())
        .join('')
    },
    conversationPreview(conversation) {
      if (conversation.isTyping) return 'Typing...'
      if (!conversation.lastMessageBody) return 'No messages yet.'
      if (conversation.lastMessageSenderId === this.userId) {
        return `You: ${conversation.lastMessageBody}`
      }
      return conversation.lastMessageBody
    },
    formatMessage(message) {
      const isMine = message.sender_id === this.userId
      return {
        ...message,
        isMine,
        createdLabel: new Date(message.created_at).toLocaleString(),
        timeLabel: new Date(message.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
        statusLabel: message.seen_at ? 'Seen' : message.delivered_at ? 'Delivered' : 'Sent',
      }
    },
    relativeTimeLabel(value) {
      if (!value) return 'just now'
      const date = new Date(value)
      const diffMs = Date.now() - date.getTime()
      const diffMinutes = Math.max(0, Math.floor(diffMs / 60000))
      if (diffMinutes < 1) return 'just now'
      if (diffMinutes < 60) return `${diffMinutes}m ago`
      const diffHours = Math.floor(diffMinutes / 60)
      if (diffHours < 24) return `${diffHours}h ago`
      const diffDays = Math.floor(diffHours / 24)
      if (diffDays < 7) return `${diffDays}d ago`
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
    },
    messageReceiptLabel(message) {
      if (message.seen_at) return `Seen ${this.relativeTimeLabel(message.seen_at)}`
      if (message.delivered_at) return `Delivered ${this.relativeTimeLabel(message.delivered_at)}`
      return `Sent ${this.relativeTimeLabel(message.created_at)}`
    },
    conversationActivityLabel(conversation) {
      if (conversation.isTyping) return 'Typing now'
      if (conversation.lastActiveAt && conversation.lastActiveAt !== conversation.lastMessageAt) {
        return `Active ${this.relativeTimeLabel(conversation.lastActiveAt)}`
      }
      if (conversation.lastMessageAt) return `Updated ${this.relativeTimeLabel(conversation.lastMessageAt)}`
      return 'No recent activity'
    },
    dayLabel(value) {
      const date = new Date(value)
      const today = new Date()
      const yesterday = new Date()
      yesterday.setDate(today.getDate() - 1)
      if (date.toDateString() === today.toDateString()) return 'Today'
      if (date.toDateString() === yesterday.toDateString()) return 'Yesterday'
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
    },
    retentionLabel(hours) {
      if (hours === 3) return '3 Hr'
      if (hours === 24) return '24 Hr'
      return '1 Week'
    },
    retentionButtonClass(hours) {
      const isActive = Number(this.activeConversation?.retentionHours || 168) === Number(hours)
      if (isActive) {
        return 'border-neon-400/40 bg-neon-400/15 text-neon-300'
      }
      return 'border-white/10 bg-white/[0.03] text-steel-300 hover:border-white/20 hover:bg-white/[0.05] hover:text-white'
    },
    async purgeExpiredConversations() {
      await supabase.rpc('purge_expired_direct_conversations')
    },
    async fetchConversations() {
      this.loadingConversations = true
      this.errorMessage = ''

      let conversationRows = []
      let error = null

      const withRetentionResult = await runSupabaseQueryWithRetry(() => (
        supabase
          .from('direct_conversations')
          .select('id, user_one_id, user_two_id, created_at, updated_at, last_message_at, retention_hours')
          .or(`user_one_id.eq.${this.userId},user_two_id.eq.${this.userId}`)
          .order('last_message_at', { ascending: false })
      ))

      conversationRows = withRetentionResult.data || []
      error = withRetentionResult.error || null

      if (error && String(error.message || '').includes('retention_hours')) {
        this.retentionFeatureReady = false
        const fallbackResult = await runSupabaseQueryWithRetry(() => (
          supabase
            .from('direct_conversations')
            .select('id, user_one_id, user_two_id, created_at, updated_at, last_message_at')
            .or(`user_one_id.eq.${this.userId},user_two_id.eq.${this.userId}`)
            .order('last_message_at', { ascending: false })
        ))

        conversationRows = fallbackResult.data || []
        error = fallbackResult.error || null
      } else {
        this.retentionFeatureReady = true
      }

      this.loadingConversations = false

      if (error) {
        this.errorMessage = error.message || 'Unable to load your conversations.'
        return
      }

      const conversationIds = (conversationRows || []).map((row) => row.id)
      const otherUserIds = [...new Set((conversationRows || []).map((row) => row.user_one_id === this.userId ? row.user_two_id : row.user_one_id))]

      const [profilesResult, messagesResult, unreadResult, typingResult] = await Promise.all([
        otherUserIds.length
          ? supabase.from('profiles').select('id, handle, full_name, avatar_url, avatar_path').in('id', otherUserIds)
          : Promise.resolve({ data: [], error: null }),
        conversationIds.length
          ? supabase
            .from('direct_messages')
            .select('id, conversation_id, sender_id, recipient_id, body, created_at, delivered_at, seen_at')
            .in('conversation_id', conversationIds)
            .order('created_at', { ascending: false })
          : Promise.resolve({ data: [], error: null }),
        conversationIds.length
          ? supabase
            .from('direct_messages')
            .select('conversation_id')
            .eq('recipient_id', this.userId)
            .is('seen_at', null)
            .in('conversation_id', conversationIds)
          : Promise.resolve({ data: [], error: null }),
        conversationIds.length
          ? supabase
            .from('direct_message_typing_states')
            .select('conversation_id, user_id, is_typing, updated_at')
            .in('conversation_id', conversationIds)
          : Promise.resolve({ data: [], error: null }),
      ])

      const profileMap = new Map((profilesResult.data || []).map((profile) => [profile.id, profile]))
      const latestMessageMap = new Map()
      ;(messagesResult.data || []).forEach((message) => {
        if (!latestMessageMap.has(message.conversation_id)) {
          latestMessageMap.set(message.conversation_id, message)
        }
      })

      const unreadMap = new Map()
      ;(unreadResult.data || []).forEach((message) => {
        unreadMap.set(message.conversation_id, (unreadMap.get(message.conversation_id) || 0) + 1)
      })

      const typingMap = new Map()
      const lastActiveMap = new Map()
      ;(typingResult.data || []).forEach((state) => {
        const isFresh = Date.now() - new Date(state.updated_at).getTime() < 6000
        if (state.user_id !== this.userId && state.is_typing && isFresh) {
          typingMap.set(state.conversation_id, true)
        }
        if (state.user_id !== this.userId && state.updated_at) {
          const current = lastActiveMap.get(state.conversation_id)
          if (!current || new Date(state.updated_at) > new Date(current)) {
            lastActiveMap.set(state.conversation_id, state.updated_at)
          }
        }
      })

      this.conversations = (conversationRows || []).map((row) => {
        const otherUserId = row.user_one_id === this.userId ? row.user_two_id : row.user_one_id
        const otherUser = profileMap.get(otherUserId) || {}
        const lastMessage = latestMessageMap.get(row.id)
        const lastMessageAt = lastMessage?.created_at || row.last_message_at || row.created_at
        return {
          id: row.id,
          otherUserId,
          otherUserHandle: otherUser.handle || 'member',
          otherUserName: otherUser.full_name || 'DRVN Member',
          otherUserAvatar: getAvatarUrl({ avatarPath: otherUser.avatar_path, avatarUrl: otherUser.avatar_url }),
          otherUserInitials: this.initialsForName(otherUser.full_name || 'DRVN Member'),
          lastMessageBody: lastMessage?.body || '',
          lastMessageSenderId: lastMessage?.sender_id || '',
          lastMessageAt,
          lastMessageAtLabel: lastMessageAt ? new Date(lastMessageAt).toLocaleDateString() : '',
          retentionHours: row.retention_hours || 168,
          retentionLabel: this.retentionLabel(row.retention_hours || 168),
          unreadCount: unreadMap.get(row.id) || 0,
          isTyping: typingMap.get(row.id) || false,
          lastActiveAt: lastActiveMap.get(row.id) || lastMessageAt,
        }
      })
    },
    async fetchTypingState(conversationId) {
      if (!conversationId) return

      const { data } = await supabase
        .from('direct_message_typing_states')
        .select('conversation_id, user_id, is_typing, updated_at')
        .eq('conversation_id', conversationId)

      const otherState = (data || []).find((state) => state.user_id !== this.userId)
      const isFresh = otherState?.updated_at ? Date.now() - new Date(otherState.updated_at).getTime() < 6000 : false
      const isTyping = Boolean(otherState?.is_typing && isFresh)

      this.conversations = this.conversations.map((conversation) => (
        conversation.id === conversationId
          ? {
            ...conversation,
            isTyping,
            lastActiveAt: otherState?.updated_at || conversation.lastActiveAt,
          }
          : conversation
      ))
    },
    async fetchMessages(conversationId, options = {}) {
      if (!conversationId) {
        this.messages = []
        return
      }

      const {
        showLoader = true,
        syncConversation = true,
      } = options

      if (showLoader) {
        this.loadingMessages = true
      }

      const { data, error } = await runSupabaseQueryWithRetry(() => (
        supabase
          .from('direct_messages')
          .select('id, conversation_id, sender_id, recipient_id, body, created_at, delivered_at, seen_at')
          .eq('conversation_id', conversationId)
          .order('created_at', { ascending: true })
      ))

      if (showLoader) {
        this.loadingMessages = false
      }

      if (error) {
        this.errorMessage = error.message || 'Unable to load this conversation.'
        return
      }

      this.messages = (data || []).map((message) => this.formatMessage(message))
      await this.markConversationDelivered(conversationId)
      await this.markConversationSeen(conversationId)
      if (syncConversation) {
        await this.fetchConversations()
      }
      await this.fetchTypingState(conversationId)
      this.scrollMessagesToBottom()
    },
    async openConversation(conversationId) {
      if (!conversationId) return
      if (this.activeConversationId && this.activeConversationId !== conversationId) {
        await this.setTyping(false)
      }
      this.showConversationMenu = false
      this.activeConversationId = conversationId
      await this.fetchMessages(conversationId)
      await this.joinPresenceChannel(conversationId)
      await this.setTyping(false)
    },
    goBackToInbox() {
      this.activeConversationId = ''
      this.messages = []
      this.showConversationMenu = false
      this.leavePresenceChannel()
      if (this.$route.params.handle) {
        this.$router.replace('/messages')
      }
    },
    async openConversationFromRoute() {
      const handle = this.$route.params.handle
      if (!this.userId) return
      if (!handle) {
        this.activeConversationId = ''
        this.messages = []
        return
      }

      const { data: profile, error } = await runSupabaseQueryWithRetry(() => (
        supabase
          .from('profiles')
          .select('id, handle')
          .eq('handle', handle)
          .maybeSingle()
      ))

      if (error || !profile?.id) {
        this.errorMessage = 'Unable to open this conversation.'
        return
      }

      if (profile.id === this.userId) {
        this.$router.replace('/messages')
        return
      }

      const { data: conversationId, error: conversationError } = await supabase.rpc('get_or_create_direct_conversation', {
        p_other_user_id: profile.id,
      })

      if (conversationError || !conversationId) {
        this.errorMessage = conversationError?.message || 'Unable to start this conversation.'
        return
      }

      await this.fetchConversations()
      await this.openConversation(conversationId)
    },
    async updateConversationRetention(value) {
      if (!this.activeConversationId) return
      if (!this.retentionFeatureReady) {
        this.errorMessage = 'Run the chat retention migration in Supabase before changing auto-delete settings.'
        return
      }

      const retentionHours = Number(value)
      if (![3, 24, 168].includes(retentionHours)) return

      this.savingRetention = true
      this.errorMessage = ''

      const { error } = await supabase.rpc('set_direct_conversation_retention', {
        p_conversation_id: this.activeConversationId,
        p_retention_hours: retentionHours,
      })

      this.savingRetention = false

      if (error) {
        this.errorMessage = error.message || 'Unable to update auto-delete setting.'
        return
      }

      this.conversations = this.conversations.map((conversation) => (
        conversation.id === this.activeConversationId
          ? {
            ...conversation,
            retentionHours,
            retentionLabel: this.retentionLabel(retentionHours),
          }
          : conversation
      ))
      this.showConversationMenu = false
    },
    async deleteConversationNow() {
      if (!this.activeConversationId) return
      if (!window.confirm('Delete this conversation now? This removes the chat from the database for both users.')) return

      const deletingId = this.activeConversationId
      this.errorMessage = ''

      const { error } = await supabase.rpc('delete_direct_conversation_now', {
        p_conversation_id: deletingId,
      })

      if (error) {
        this.errorMessage = error.message || 'Unable to delete this conversation.'
        return
      }

      await this.leavePresenceChannel()
      this.activeConversationId = ''
      this.messages = []
      this.showConversationMenu = false
      await this.fetchConversations()
      this.$router.replace('/messages')
    },
    async sendMessage() {
      if (!this.activeConversation || !this.draftMessage.trim() || this.sendingMessage) return

      this.sendingMessage = true
      const body = this.draftMessage.trim()
      this.errorMessage = ''

      await this.purgeExpiredConversations()

      const { error } = await supabase
        .from('direct_messages')
        .insert({
          conversation_id: this.activeConversation.id,
          sender_id: this.userId,
          recipient_id: this.activeConversation.otherUserId,
          body,
        })

      this.sendingMessage = false

      if (error) {
        this.errorMessage = error.message || 'Unable to send your message.'
        return
      }

      this.draftMessage = ''
      await this.setTyping(false)
      await this.fetchMessages(this.activeConversation.id)
      await this.fetchConversations()
      this.scrollMessagesToBottom()
    },
    startLiveSync() {
      this.stopLiveSync()
      this.liveSyncTimer = setInterval(async () => {
        if (document.hidden || !this.activeConversationId) return
        await this.fetchMessages(this.activeConversationId, { showLoader: false, syncConversation: false })
        await this.fetchConversations()
        await this.fetchTypingState(this.activeConversationId)
      }, 2500)
    },
    stopLiveSync() {
      if (this.liveSyncTimer) {
        clearInterval(this.liveSyncTimer)
        this.liveSyncTimer = null
      }
    },
    async markConversationDelivered(conversationId) {
      if (!conversationId) return
      await supabase.rpc('mark_direct_conversation_delivered', {
        p_conversation_id: conversationId,
      })
    },
    async markConversationSeen(conversationId) {
      if (!conversationId || document.hidden) return
      await supabase.rpc('mark_direct_conversation_seen', {
        p_conversation_id: conversationId,
      })
    },
    async setTyping(isTyping) {
      if (!this.activeConversationId) return
      await supabase.rpc('set_direct_conversation_typing', {
        p_conversation_id: this.activeConversationId,
        p_is_typing: Boolean(isTyping),
      })
    },
    handleDraftInput() {
      if (!this.activeConversationId) return
      if (this.typingStopTimer) {
        clearTimeout(this.typingStopTimer)
      }

      if (!this.typingSyncTimer) {
        this.typingSyncTimer = setTimeout(async () => {
          this.typingSyncTimer = null
          await this.setTyping(true)
        }, 120)
      }

      this.typingStopTimer = setTimeout(async () => {
        await this.setTyping(false)
      }, 1800)
    },
    handleComposerKeydown(event) {
      if (!event) return
      if (this.isMobileViewport) return
      if (event.key !== 'Enter') return
      if (event.shiftKey) return

      event.preventDefault()
      this.sendMessage()
    },
    clearTypingTimers() {
      if (this.typingStopTimer) {
        clearTimeout(this.typingStopTimer)
        this.typingStopTimer = null
      }
      if (this.typingSyncTimer) {
        clearTimeout(this.typingSyncTimer)
        this.typingSyncTimer = null
      }
    },
    async joinPresenceChannel(conversationId) {
      await this.leavePresenceChannel()
      if (!conversationId) return

      this.isOtherUserOnline = false
      this.presenceChannel = supabase.channel(`direct-presence-${conversationId}`, {
        config: {
          presence: {
            key: this.userId,
          },
        },
      })

      this.presenceChannel
        .on('presence', { event: 'sync' }, () => {
          const state = this.presenceChannel.presenceState()
          const otherIds = Object.keys(state || {}).filter((id) => id !== this.userId)
          this.isOtherUserOnline = otherIds.length > 0
        })
        .subscribe(async (status) => {
          if (status === 'SUBSCRIBED') {
            await this.presenceChannel.track({
              user_id: this.userId,
              online_at: new Date().toISOString(),
            })
          }
        })
    },
    async leavePresenceChannel() {
      if (this.presenceChannel) {
        try {
          await this.presenceChannel.untrack()
        } catch (_error) {
          // no-op
        }
        supabase.removeChannel(this.presenceChannel)
        this.presenceChannel = null
      }
      this.isOtherUserOnline = false
    },
    scrollMessagesToBottom() {
      this.$nextTick(() => {
        const scroller = this.$refs.messagesScroller
        if (scroller) {
          scroller.scrollTop = scroller.scrollHeight
        }
      })
    },
    async handleVisibilityChange() {
      if (document.hidden) return

      const session = await refreshReadySession()
      const userId = session?.user?.id || ''
      if (!userId) {
        this.errorMessage = 'Please sign in to use messages.'
        return
      }

      const userChanged = userId !== this.userId
      this.userId = userId
      if (userChanged) {
        this.activeConversationId = ''
        this.messages = []
      }

      await this.fetchMyProfile()
      await this.purgeExpiredConversations()
      await this.fetchConversations()
      await this.openConversationFromRoute()
      this.unsubscribeRealtime()
      this.subscribeToRealtime()

      if (this.activeConversationId) {
        await this.markConversationSeen(this.activeConversationId)
        await this.fetchMessages(this.activeConversationId)
        await this.joinPresenceChannel(this.activeConversationId)
      }
    },
    upsertLiveMessage(rawMessage) {
      if (!rawMessage?.id) return

      const formattedMessage = this.formatMessage(rawMessage)
      const existingIndex = this.messages.findIndex((message) => message.id === rawMessage.id)

      if (existingIndex >= 0) {
        this.messages.splice(existingIndex, 1, formattedMessage)
      } else {
        this.messages = [...this.messages, formattedMessage].sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      }

      this.scrollMessagesToBottom()
    },
    updateConversationPreviewFromMessage(rawMessage) {
      if (!rawMessage?.conversation_id) return

      this.conversations = this.conversations
        .map((conversation) => (
          conversation.id === rawMessage.conversation_id
            ? {
              ...conversation,
              lastMessageBody: rawMessage.body || conversation.lastMessageBody,
              lastMessageSenderId: rawMessage.sender_id || conversation.lastMessageSenderId,
              lastMessageAt: rawMessage.created_at || conversation.lastMessageAt,
              lastMessageAtLabel: rawMessage.created_at ? new Date(rawMessage.created_at).toLocaleDateString() : conversation.lastMessageAtLabel,
              unreadCount: rawMessage.recipient_id === this.userId && !rawMessage.seen_at
                ? (conversation.id === this.activeConversationId ? 0 : (conversation.unreadCount || 0) + 1)
                : conversation.unreadCount,
            }
            : conversation
        ))
        .sort((a, b) => new Date(b.lastMessageAt || 0) - new Date(a.lastMessageAt || 0))
    },
    updateTypingState(conversationId, isTyping) {
      this.conversations = this.conversations.map((conversation) => (
        conversation.id === conversationId
          ? { ...conversation, isTyping, lastActiveAt: new Date().toISOString() }
          : conversation
      ))
    },
    subscribeToRealtime() {
      this.messagesChannel = supabase
        .channel(`direct-messages-${this.userId}`)
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'direct_messages' }, async (payload) => {
          const message = payload?.new
          const conversationId = message?.conversation_id
          if (!conversationId) return

          this.updateConversationPreviewFromMessage(message)

          if (conversationId === this.activeConversationId) {
            this.upsertLiveMessage(message)
            await this.markConversationDelivered(conversationId)
            await this.markConversationSeen(conversationId)
            this.conversations = this.conversations.map((conversation) => (
              conversation.id === conversationId
                ? { ...conversation, unreadCount: 0 }
                : conversation
            ))
          } else {
            await this.fetchConversations()
          }
        })
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'direct_messages' }, async (payload) => {
          const message = payload?.new
          const conversationId = message?.conversation_id
          if (!conversationId) return

          if (conversationId === this.activeConversationId) {
            this.upsertLiveMessage(message)
          }

          await this.fetchConversations()
        })
        .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'direct_messages' }, async () => {
          await this.fetchConversations()
          if (this.activeConversationId) {
            await this.fetchMessages(this.activeConversationId)
          }
        })
        .subscribe()

      this.typingChannel = supabase
        .channel(`direct-typing-${this.userId}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'direct_message_typing_states' }, async (payload) => {
          const state = payload?.new || payload?.old
          if (!state?.conversation_id || state.user_id === this.userId) return
          const isFresh = state.updated_at ? Date.now() - new Date(state.updated_at).getTime() < 6000 : false
          this.updateTypingState(state.conversation_id, Boolean(state.is_typing && isFresh))
        })
        .subscribe()

      this.conversationsChannel = supabase
        .channel(`direct-conversations-${this.userId}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'direct_conversations' }, async () => {
          await this.purgeExpiredConversations()
          await this.fetchConversations()
          if (this.activeConversationId && !this.conversations.some((conversation) => conversation.id === this.activeConversationId)) {
            this.activeConversationId = ''
            this.messages = []
          }
        })
        .subscribe()
    },
    unsubscribeRealtime() {
      this.leavePresenceChannel()
      if (this.messagesChannel) {
        supabase.removeChannel(this.messagesChannel)
        this.messagesChannel = null
      }
      if (this.typingChannel) {
        supabase.removeChannel(this.typingChannel)
        this.typingChannel = null
      }
      if (this.conversationsChannel) {
        supabase.removeChannel(this.conversationsChannel)
        this.conversationsChannel = null
      }
    },
  },
}
</script>
